const staticText = {
  heading: 'Webhooks',
  description: 'Receive vehicle data when signals change based on your preferences.',
  addWebhookButton: 'Add webhook',
  buttonText: 'Create your first webhook',
  emptyState: {
    text: 'No webhooks found',
    cta: {
      text: 'Add webhook',
    },
  },
  resourceCards: [
    {
      title: 'Documentation',
      text: 'Learn how to register, verify, disable, and test webhooks.',
      // TODO: will need to update soon with release of signals-based Webhooks
      link: 'https://smartcar.com/docs/getting-started/tutorials/webhooks-scheduled',
      linkText: 'See the guides',
    },
    {
      title: 'API Reference',
      text:
        'See which endpoints to use when registering and deleting webhooks.',
      link: 'https://smartcar.com/docs/api-reference/intro',
      linkText: 'See the endpoints',
    },
  ],
  featureGated: {
    description: 'Upgrade to add webhooks & more',
    contactOwnersToUpgrade: 'Contact a team owner to upgrade to add webhooks & more',
    contactOwnersToRegainAccess: 'Contact a team owner to upgrade to regain access to your webhooks',
    buttonText: 'Upgrade to add webhooks',
    regainAccessDescription: '|+|Upgrade your plan@@/team/billing|+| to regain access to your webhooks',
  },
  errorConfig: {
    title: 'Unable to add new webhook',
    errorMessage: 'Your application can have a maximum of ten webhooks associated with it. Please delete one of your webhooks in order to add a new one.',
    buttonText: 'Close',
  },
  webhooksTable: {
    headers: {
      status: 'Status',
      name: 'Name',
      lastUpdated: 'Last updated',
    },
  },
  addModal: {
    title: 'Add webhook',
    edit: 'Edit webhook',
    back: 'Back',
    next: 'Next',
    addOnly: 'Add webhook only',
    addAndVerify: 'Add and verify webhook',
    verifyLater: 'Verify later',
    update: 'Update webhook',
    updateOnly: 'Update webhook only',
    updateAndVerify: 'Update and verify webhook',
    stepperSections: [
      {
        title: 'Choose a template',
        description: 'To get started, select a template from below or create a custom webhook:',
      },
      {
        title: 'Signals',
        description: 'Select which vehicle signal(s) you would like to receive:',
        search: {
          label: 'Search signals',
          emptyState: {
            title: 'No vehicle signals found',
            description: 'Try another search query',
          },
        },
        tabs: [
          { label: 'All' },
          { label: 'Selected' },
        ],
        warning: {
          title: 'All signals selected',
          description: 'This may result in a large webhook payload. Consider selecting only the signals you need before continuing.',
        },
      },
      {
        title: 'Details',
        fields: [
          {
            key: 'rules',
            name: 'Condition',
            description: 'The exact frequency varies depending on the vehicle brand. Additional conditions coming soon.',
            inputLabel: 'When',
            type: 'select',
            options: [
              { label: 'ASAP', value: 'asap' },
            ],
            tooltip: "When a vehicle is first subscribed to a webhook, Smartcar will send an initial payload that attempts to get all the signals you're subscribed to. Smartcar will only send subsequent data for signals that have changed from the previous payload.",
          },
          {
            key: 'name',
            name: 'Webhook name',
            inputPlaceholder: 'Enter webhook name',
            type: 'text',
            errorText: 'Must be between 3 and 30 characters',
            required: true,
          },
          {
            key: 'callbackUri',
            name: 'Vehicle data callback URI',
            description: 'The destination that your vehicle data will be sent to.',
            inputPlaceholder: 'https://example.com',
            type: 'text',
            errorText: 'Must be a valid URI starting with https://',
            required: true,
          },
          {
            key: 'errorCallbackUri',
            name: 'Vehicle error callback URI',
            description: 'Errors are sent to the same vehicle data callback URI with a different "type" field in the payloads. Enter a different URI if you wish to receive errors separately.',
            inputPlaceholder: 'https://example.com',
            type: 'text',
            errorText: 'Must be a valid URI starting with https://',
            required: false,
            fallback: '(Same as vehicle data callback URI)',
          },
          {
            key: 'units',
            name: 'Units',
            description: "Select which units you'd like to receive events in from the vehicle:",
            // inputPlaceholder: 'Select units',
            type: 'select',
            options: [
              { label: 'Metric', value: 'metric' },
              { label: 'Imperial', value: 'imperial' },
            ],
            required: true,
          },
          {
            key: 'autoSubscribe',
            name: 'Vehicle subscription',
            description: 'Select a vehicle subscription option for this webhook. You can edit this later.',
            inputPlaceholder: 'Make a selection',
            type: 'select',
            options: [
              { label: 'Don’t subscribe any vehicles', value: 'none' },
              { label: 'Subscribe all current {{vehicleCount}} and new connections moving forward', value: 'all' },
            ],
            required: true,
          },
        ],
      },
      {
        title: 'Review',
        fieldOrder: [
          'name',
          'callbackUri',
          'errorCallbackUri',
          'units',
          'rules',
          'autoSubscribe',
        ],
        vehicleSignals: 'Vehicle signals',
        payload: {
          title: 'Test Response Body',
        },
      },
      {
        title: 'Verify',
        verification: 'To verify this webhook, Smartcar will send a challenge to your callback URI. It will contain the following string:',
        code: 'nsQsLU7wY9WKt2IU',
        documentation: 'Your code should generate a signature containing your management token and the challenge string. See the |+|API reference@@https://smartcar.com/docs/api-reference/webhooks/callback-verification|+| for details.',
        skip: 'You can also add this webhook and verify later.',
        reverification: {
          required: 'Editing the callback URI or error callback URI will require re-verification of this webhook.',
          notRequired: 'This webhook has already been verified, no further action is needed.',
        },
      },
    ],
    customTemplate: {
      name: 'custom',
      displayName: 'Custom',
      description: 'Custom webhook template',
    },
    exitModal: {
      title: 'Are you sure you want to exit?',
      description: 'You will lose any progress for the current webhook.',
      cancel: 'Keep editing',
      confirm: 'Exit',
    },
    errors: {
      fetchingSignals: 'Error fetching signals. Please try again later.',
      savingWebhook: 'Error saving webhook. Please try again later.',
      verifyingWebhook: 'Error verifying webhook. Please try again later.',
    },
  },
  webhookDetails: {
    data: [
      {
        label: 'Status',
        type: 'chip',
        key: 'status',
      },
      // {
      //   label: 'Last delivered',
      //   type: 'text',
      //   key: 'deliveryTime',
      // },
      {
        label: 'Subscribed vehicles',
        type: 'text',
        key: 'subscribedVehicles',
        fallback: 'No vehicles are subscribed to this webhook. |+|See the guide@@https://smartcar.com/docs/api-reference/webhooks/subscribe-webhook|+| to learn how to subscribe vehicles to a webhook.',
      },
      {
        label: 'Vehicle subscription',
        type: 'text',
        key: 'autoSubscribe',
      },
      {
        label: 'Vehicle data callback URI',
        type: 'snippet',
        key: 'callbackUri',
      },
      {
        label: 'Vehicle error callback URI',
        type: 'snippet',
        key: 'errorCallbackUri',
      },
      {
        label: 'Webhook ID',
        type: 'snippet',
        key: 'id',
      },
      {
        label: 'Units',
        type: 'text',
        key: 'units',
      },
      {
        label: 'Last edited',
        type: 'text',
        key: 'updatedAt',
      },
    ],
    signals: 'Signals',
    permissions: 'Permissions',
    errorCallbackUriFallback: '(Same as vehicle data callback URI)',
    payload: {
      title: 'Data payload',
      errorTitle: 'Error payload',
      fetchError: 'Error fetching payload. Please try again later.',
    },
  },
};

export const messages = {
  webhookCreated: 'Webhook created successfully!',
  webhookDeleted: 'Webhook deleted successfully!',
  webhookUpdated: 'Webhook updated successfully!',
};

export const errors = {
  webhookCreation: 'Failed to create webhook',
  webhookDeletion: 'Failed to delete webhook',
  webhookUpdate: 'Failed to update webhook',
};

export default staticText;
